import React, { useEffect, useState } from 'react';
import {
  collection, doc, getDoc, getDocs, query, where, orderBy,
  serverTimestamp, updateDoc, setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { Client, CheckIn, CheckinReason, AppSettings, DEFAULT_SETTINGS, clientDisplayName, sellerDisplayName } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface Props {
  client: Client;
  onClose: () => void;
  onDone: () => void;
}

// Distância entre dois pontos GPS em metros (Haversine) — espelhado do app
function distanceInMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function CheckinModal({ client, onClose, onDone }: Props) {
  const { user } = useAuth();
  const [openCheckin, setOpenCheckin] = useState<CheckIn | null>(null);
  const [reasons, setReasons] = useState<CheckinReason[]>([]);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [notes, setNotes] = useState('');
  const [location, setLocation] = useState<{ latitude: number; longitude: number; accuracy?: number } | null>(null);
  const [locError, setLocError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [resultado, setResultado] = useState<'comprador_atendeu' | 'comprador_ausente' | 'comprador_nao_atendeu' | null>(null);
  const [pedidoStatus, setPedidoStatus] = useState<'sim' | 'nao' | 'orcamento'>('nao');
  const [pedidoValor, setPedidoValor] = useState('');
  const [cooldownDias, setCooldownDias] = useState(60);

  useEffect(() => {
    const load = async () => {
      // Visita em aberto deste vendedor neste cliente?
      const q1 = query(
        collection(db, 'checkins'),
        where('sellerId', '==', user!.id),
        where('clientId', '==', client.id),
        where('checkoutAt', '==', null)
      );
      const snap = await getDocs(q1);
      if (!snap.empty) {
        const d = snap.docs[0];
        const raw = d.data();
        setOpenCheckin({ id: d.id, ...raw, createdAt: raw.createdAt?.toDate() ?? new Date() } as CheckIn);
      }

      // Motivos ativos + configurações
      const [reasonsSnap, settingsSnap, rotSnap] = await Promise.all([
        getDocs(query(collection(db, 'checkinReasons'), orderBy('name'))),
        getDoc(doc(db, 'settings', 'app')),
        getDoc(doc(db, 'settings', 'roteirizacao')),
      ]);
      setReasons((reasonsSnap.docs.map(d => ({ id: d.id, ...d.data() })) as CheckinReason[]).filter(r => r.active));
      if (settingsSnap.exists()) setSettings({ ...DEFAULT_SETTINGS, ...settingsSnap.data() } as AppSettings);
      if (rotSnap.exists()) setCooldownDias(Number(rotSnap.data().cooldownContatoFrioDias ?? 60));
      setLoading(false);
    };
    load();

    // GPS do navegador
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        () => setLocError('Não foi possível obter sua localização. Permita o acesso no navegador.'),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    } else {
      setLocError('Seu navegador não suporta geolocalização.');
    }
  }, [client.id, user]);

  const toggleReason = (name: string) =>
    setSelectedReasons(prev => (prev.includes(name) ? prev.filter(r => r !== name) : [...prev, name]));

  const handleCheckin = async () => {
    setError('');
    if (!location) {
      setError('Aguarde a localização ser obtida (ou permita o acesso).');
      return;
    }
    if (photos.length === 0) {
      setError('Anexe pelo menos uma foto do check-in.');
      return;
    }
    // Regra do gestor: distância máxima. NÃO bloqueia de vez (a coordenada do cliente pode
    // estar errada/imprecisa) — avisa e deixa confirmar, registrando o desvio.
    let foraDoRaio = false;
    let distanciaMetros: number | null = null;
    const temCoord = !!(client.location?.latitude && client.location?.longitude);
    if (temCoord) {
      distanciaMetros = Math.round(distanceInMeters(location.latitude, location.longitude, client.location!.latitude, client.location!.longitude));
    }
    if (settings.checkinRadiusEnabled && distanciaMetros != null && distanciaMetros > settings.checkinRadiusMeters) {
      if (!window.confirm(`Você está a ${distanciaMetros}m do cliente (limite ${settings.checkinRadiusMeters}m). Isso pode ser uma coordenada errada do cliente. Fazer o check-in mesmo assim?`)) return;
      foraDoRaio = true;
    }

    setSaving(true);
    try {
      // Trava anti-duplicidade: já existe visita em aberto deste vendedor neste cliente?
      const abertaSnap = await getDocs(query(
        collection(db, 'checkins'),
        where('sellerId', '==', user!.id),
        where('clientId', '==', client.id),
        where('checkoutAt', '==', null),
      ));
      if (!abertaSnap.empty) {
        setError('Já existe uma visita em aberto neste cliente. Faça o check-out antes de iniciar outra.');
        setSaving(false);
        return;
      }

      // Cria a referência antes para usar o id no caminho das fotos
      const checkinRef = doc(collection(db, 'checkins'));

      const photoUrls: string[] = [];
      for (let i = 0; i < photos.length; i++) {
        const storageRef = ref(storage, `checkins/${checkinRef.id}/${Date.now()}_${i}.jpg`);
        await uploadBytes(storageRef, photos[i]);
        photoUrls.push(await getDownloadURL(storageRef));
      }

      await setDoc(checkinRef, {
        sellerId: user!.id,
        sellerName: sellerDisplayName(user!),
        clientId: client.id,
        clientName: clientDisplayName(client),
        location,
        notes: null,
        reasons: [],
        foraDoRaio,
        distanciaMetros,
        photos: photoUrls,
        checkoutAt: null,
        createdAt: serverTimestamp(),
      });

      // Verdade de campo: se não há coordenada ou está muito longe (provável coordenada
      // errada), oferecer corrigir a localização do cliente para a posição atual do GPS.
      const semCoord = !temCoord;
      const limiteOferta = settings.checkinRadiusEnabled ? settings.checkinRadiusMeters : 500;
      const muitoLonge = distanciaMetros != null && distanciaMetros > limiteOferta;
      const gpsOk = location.accuracy == null || location.accuracy <= 150;
      if ((semCoord || muitoLonge) && gpsOk) {
        const msg = semCoord
          ? 'Este cliente ainda não tem localização no mapa. Você está nele agora — salvar a localização do cliente na sua posição atual?'
          : `A localização cadastrada do cliente está a ${(distanciaMetros! / 1000).toFixed(1)} km daqui — provavelmente errada. Corrigir a localização do cliente para a sua posição atual?`;
        if (window.confirm(msg)) {
          try {
            await updateDoc(doc(db, 'clients', client.id), {
              location: { latitude: location.latitude, longitude: location.longitude },
              geoPrecision: 'gps',
              locationSource: 'checkin',
              locationUpdatedBy: user!.id,
              locationUpdatedAt: serverTimestamp(),
            });
          } catch { /* não bloqueia o check-in já gravado */ }
        }
      }
      onDone();
    } catch {
      setError('Não foi possível registrar o check-in. Tente novamente.');
      setSaving(false);
    }
  };

  const handleCheckout = async () => {
    if (!openCheckin) return;
    setError('');
    if (!resultado) {
      setError('Selecione o resultado da visita.');
      return;
    }
    if (reasons.length > 0 && selectedReasons.length === 0) {
      setError('Selecione pelo menos um motivo do check-out.');
      return;
    }
    if (photos.length === 0) {
      setError('Anexe pelo menos uma foto do check-out.');
      return;
    }
    setSaving(true);
    try {
      const photoUrls: string[] = [];
      for (let i = 0; i < photos.length; i++) {
        const storageRef = ref(storage, `checkins/${openCheckin.id}/checkout_${Date.now()}_${i}.jpg`);
        await uploadBytes(storageRef, photos[i]);
        photoUrls.push(await getDownloadURL(storageRef));
      }
      const temValor = pedidoStatus === 'sim' || pedidoStatus === 'orcamento';
      const valorNum = temValor ? (Number(String(pedidoValor).replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')) || 0) : null;
      await updateDoc(doc(db, 'checkins', openCheckin.id), {
        checkoutAt: serverTimestamp(),
        checkoutLocation: location || null,
        reasons: selectedReasons,
        checkoutPhotos: photoUrls,
        notes: notes.trim() || null,
        resultado,
        pedidoStatus,
        pedidoFeito: pedidoStatus === 'sim',
        pedidoValor: valorNum,
      });
      // Realimenta a roteirização: cooldown quando não houve receptividade
      const clientPatch: any = { ultimoResultado: resultado };
      clientPatch.cooldownAte = resultado === 'comprador_atendeu' ? null : new Date(Date.now() + cooldownDias * 86400000);
      try { await updateDoc(doc(db, 'clients', client.id), clientPatch); } catch { /* não bloqueia o check-out */ }
      onDone();
    } catch {
      setError('Não foi possível registrar o check-out. Tente novamente.');
      setSaving(false);
    }
  };

  // UI de fotos (reutilizada no check-in e no check-out)
  const photosField = (
    <>
      <label style={styles.label}>Fotos *</label>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={e => {
          const files = Array.from(e.target.files || []);
          if (files.length) setPhotos(prev => [...prev, ...files]);
          e.target.value = '';
        }}
        style={styles.fileInput}
      />
      {photos.length > 0 && (
        <div style={styles.thumbsWrap}>
          {photos.map((f, i) => (
            <div key={i} style={styles.thumbBox}>
              <img src={URL.createObjectURL(f)} alt="" style={styles.thumb} />
              <button type="button" style={styles.thumbRemove} onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}>✕</button>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const isCheckout = !!openCheckin;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>{isCheckout ? 'Check-out' : 'Check-in'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.body}>
          <p style={styles.clientName}>{clientDisplayName(client)}</p>

          {/* Contato principal do Sankhya (TGFCTT): por quem perguntar na loja */}
          {client.sankhya?.contatoNome && (
            <p style={{ fontSize: 13, color: '#555', margin: '0 0 6px' }}>👤 {client.sankhya.contatoNome}</p>
          )}
          {client.phone && (
            <div style={styles.contactRow}>
              <a href={`tel:${client.phone.replace(/\D/g, '')}`} style={styles.contactBtn}>📞 Ligar</a>
              <a href={`https://wa.me/55${client.phone.replace(/\D/g, '').replace(/^0+/, '')}`} target="_blank" rel="noreferrer" style={styles.contactBtn}>💬 WhatsApp</a>
            </div>
          )}
          {/* Telefones alternativos do ERP (AD_TELEF2/3/4 + televendas) */}
          {(client.sankhya?.telefones?.length ?? 0) > 0 && (
            <div style={{ ...styles.contactRow, flexWrap: 'wrap' as const }}>
              {(client.sankhya?.telefones || []).map((t) => (
                <a key={t} href={`tel:${String(t).replace(/\D/g, '')}`} style={styles.contactBtn}>📞 {t}</a>
              ))}
            </div>
          )}

          <div style={styles.locBox}>
            {location ? (
              <span style={{ color: '#16a34a', fontSize: 13 }}>
                ✅ Localização obtida ({location.latitude.toFixed(5)}, {location.longitude.toFixed(5)})
              </span>
            ) : locError ? (
              <span style={{ color: '#dc2626', fontSize: 13 }}>⚠️ {locError}</span>
            ) : (
              <span style={{ color: '#f5821f', fontSize: 13 }}>⏳ Obtendo localização...</span>
            )}
          </div>

          {loading ? (
            <p>Carregando...</p>
          ) : isCheckout ? (
            <>
              <div style={styles.checkoutInfo}>
                <p style={{ fontSize: 14, color: '#444', margin: 0 }}>
                  Você tem uma visita em aberto neste cliente desde{' '}
                  <strong>{openCheckin!.createdAt.toLocaleString('pt-BR')}</strong>.
                </p>
              </div>

              <label style={styles.label}>Resultado da visita *</label>
              <div style={styles.reasonsWrap}>
                {([['comprador_ausente', 'Comprador Ausente'], ['comprador_atendeu', 'Comprador Atendeu'], ['comprador_nao_atendeu', 'Comprador Não Atendeu']] as const).map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setResultado(v)} style={{ ...styles.reasonChip, ...(resultado === v ? styles.reasonChipActive : {}) }}>{l}</button>
                ))}
              </div>

              <label style={styles.label}>Fez pedido?</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <button type="button" onClick={() => setPedidoStatus('sim')} style={{ ...styles.reasonChip, ...(pedidoStatus === 'sim' ? styles.reasonChipActive : {}) }}>Sim</button>
                <button type="button" onClick={() => { setPedidoStatus('nao'); setPedidoValor(''); }} style={{ ...styles.reasonChip, ...(pedidoStatus === 'nao' ? styles.reasonChipActive : {}) }}>Não</button>
                <button type="button" onClick={() => setPedidoStatus('orcamento')} style={{ ...styles.reasonChip, ...(pedidoStatus === 'orcamento' ? styles.reasonChipActive : {}) }}>Fiz Orçamento</button>
                {(pedidoStatus === 'sim' || pedidoStatus === 'orcamento') && (
                  <input
                    style={{ flex: 1, minWidth: 120, border: '1px solid #e0e0e0', borderRadius: 8, padding: '8px 12px', fontSize: 14 }}
                    placeholder={pedidoStatus === 'orcamento' ? 'Valor do orçamento (R$)' : 'Valor do pedido (R$)'}
                    value={pedidoValor}
                    onChange={e => setPedidoValor(e.target.value)}
                  />
                )}
              </div>

              {reasons.length > 0 && (
                <>
                  <label style={styles.label}>Motivos da visita *</label>
                  <div style={styles.reasonsWrap}>
                    {reasons.map(r => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => toggleReason(r.name)}
                        style={{
                          ...styles.reasonChip,
                          ...(selectedReasons.includes(r.name) ? styles.reasonChipActive : {}),
                        }}
                      >
                        {r.name}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <label style={styles.label}>Resumo / observação da visita (opcional)</label>
              <textarea
                style={styles.textarea}
                placeholder="Resumo da visita, próximos passos, combinados..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />

              {photosField}
            </>
          ) : (
            <>
              {photosField}
            </>
          )}

          {error && <div style={styles.error}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancelar</button>
          {isCheckout ? (
            <button style={{ ...styles.saveBtn, background: '#dc2626' }} onClick={handleCheckout} disabled={saving}>
              {saving ? 'Salvando...' : '✔ Confirmar Check-out'}
            </button>
          ) : (
            <button style={styles.saveBtn} onClick={handleCheckin} disabled={saving || !location}>
              {saving ? 'Salvando...' : '📍 Confirmar Check-in'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, width: '90%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #f0f0f0' },
  title: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' },
  body: { padding: '16px 24px', overflowY: 'auto', flex: 1 },
  clientName: { fontSize: 16, fontWeight: 700, color: '#1a1a2e', margin: '0 0 10px' },
  contactRow: { display: 'flex', gap: 8, marginBottom: 14 },
  contactBtn: { flex: 1, textAlign: 'center', textDecoration: 'none', background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 8, padding: '9px 12px', fontSize: 14, fontWeight: 600 },
  locBox: { background: '#f9fafb', borderRadius: 8, padding: '10px 14px', marginBottom: 16 },
  checkoutInfo: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 16px' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 8, marginTop: 8 },
  reasonsWrap: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  reasonChip: { border: '1px solid #cbd5e1', background: '#fff', borderRadius: 20, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: '#444' },
  reasonChipActive: { background: '#f5821f', borderColor: '#f5821f', color: '#fff', fontWeight: 600 },
  textarea: { width: '100%', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box', minHeight: 80, fontFamily: 'inherit', resize: 'vertical' },
  error: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginTop: 12, fontSize: 14 },
  fileInput: { display: 'block', fontSize: 13, marginBottom: 8 },
  thumbsWrap: { display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4, marginBottom: 8 },
  thumbBox: { position: 'relative' },
  thumb: { width: 72, height: 72, borderRadius: 8, objectFit: 'cover', border: '1px solid #e5e7eb' },
  thumbRemove: { position: 'absolute', top: -6, right: -6, background: '#dc2626', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 11, lineHeight: 1 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 24px', borderTop: '1px solid #f0f0f0' },
  cancelBtn: { background: '#f3f4f6', color: '#444', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  saveBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
};
